/**
 * 1.6.0 AI content description (plan Part C; docs/dev/mcp-1.6.0-build.md, stream A): the API routes behind the shot
 * and segment MCP tools, segment and metadata suggestions with Accept and Reject, signed media links, and the rule that
 * nothing an AI writes ever replaces what a person wrote.
 */
import { z } from 'zod';
import { MEDIA_FRAME_LONG_SIDE, MEDIA_LINK_TTL, type LinkFunctionName } from './automation-eval.ts';

/* ---------------------------------------------------------------- who wrote a segment */

/** suggested_by: `mcp:<token name>` or `flow:<run id>` (timeline_segments.suggested_by, metadata_suggestions.suggested_by). */
export const SUGGESTED_BY = /^(mcp|flow):.{1,200}$/;
export const suggestedByMcp = (tokenName: string) => `mcp:${tokenName.slice(0, 200)}`;
export const suggestedByFlow = (runId: string) => `flow:${runId}`;

/** A segment as far as the rule needs it (SegmentDTO has these fields from 1.6.0). */
export interface SegmentAuthorship {
  text: string;
  values: string[];
  source?: string | null;
  suggestedBy?: string | null;
  acceptedAt?: string | null;
}

/** A pending suggestion: written by an AI, not accepted (accepting, or a person's edit, makes it a person's). */
export const isSuggestion = (s: SegmentAuthorship) => Boolean(s.suggestedBy) && !s.acceptedAt;

/**
 * Whether an AI (write_segment, Update segment) may write into a segment: a pending suggestion, or an empty segment
 * Fill from shots made (source 'shots', no text, no values). Anything else is a person's: SEGMENT_HUMAN.
 */
export function aiMayWriteSegment(s: SegmentAuthorship): boolean {
  if (isSuggestion(s)) return true;
  return s.source === 'shots' && !s.suggestedBy && s.text === '' && s.values.length === 0;
}

/* ---------------------------------------------------------------- shots for an assistant */

/** GET /api/v1/assets/:id/shots?offset&limit (asset.read): the current version's shots with the dialogue in each. */
export const ShotListQuery = z.object({
  offset: z.coerce.number().int().min(0).default(0),
  limit: z.coerce.number().int().min(1).max(200).default(50),
  /** A subtitle or transcript language; default: the first track. */
  lang: z.string().max(20).optional(),
}).strict();
export type ShotListQuery = z.infer<typeof ShotListQuery>;

export interface ShotItemDTO {
  /** 0-based on the Shots layer. */
  index: number;
  startTicks: number;
  endTicks: number;
  startTimecode: string;
  endTimecode: string;
  durationSeconds: number;
  /** The transcript's and subtitles' text inside the shot (cues that overlap it), at most 2,000 characters. */
  text: string;
}

export interface ShotListDTO {
  assetId: string;
  versionId: string;
  total: number;
  offset: number;
  items: ShotItemDTO[];
  /** Where the text came from: a subtitle track's label, a transcript, or none. */
  textSource: string | null;
}

/**
 * GET /api/v1/assets/:id/shots/:index/frames?count=1..3 (asset.read): the shot's frames from the proxy at its start,
 * middle and end (one: the middle; two: start and end), JPEG, MEDIA_FRAME_LONG_SIDE (768) px on the long side, cached
 * on the collateral tier. 404 SHOT_NOT_FOUND, 409 NO_PROXY / NO_SHOTS.
 */
export const ShotFramesQuery = z.object({
  count: z.coerce.number().int().min(1).max(3).default(1),
}).strict();
export interface ShotFrameDTO {
  position: 'start' | 'middle' | 'end';
  seconds: number;
  timecode: string;
  width: number;
  height: number;
  mimeType: 'image/jpeg';
  /** Base64 JPEG (the MCP program hands it to the assistant as image content). */
  data: string;
}
export interface ShotFramesDTO {
  assetId: string;
  index: number;
  frames: ShotFrameDTO[];
}
export const FRAME_LONG_SIDE = MEDIA_FRAME_LONG_SIDE;

/* ---------------------------------------------------------------- writing segments */

/**
 * POST /api/v1/assets/:id/segments/suggest (asset.edit_metadata; with an assistant token also its writeLog): write a
 * suggestion into a log layer, by span or by shot. Fills a segment the AI may write (aiMayWriteSegment) with the same
 * span, else makes one; a person's segment there answers 409 SEGMENT_HUMAN. Audited (asset.segments.suggest, via mcp).
 */
export const SuggestSegmentInput = z.object({
  stratumId: z.string().regex(/^s_[a-z0-9_]{1,40}$/),
  span: z.union([
    z.object({ startTicks: z.number().int().nonnegative(), endTicks: z.number().int().positive() }).strict(),
    z.object({ shotIndex: z.number().int().nonnegative() }).strict(),
  ]),
  text: z.string().max(5000).optional(),
  values: z.array(z.string().min(1).max(80)).max(50).optional(),
}).strict().refine((v) => v.text !== undefined || v.values !== undefined, 'Give text or values');
export type SuggestSegmentInput = z.infer<typeof SuggestSegmentInput>;
export interface SuggestSegmentResult {
  segmentId: string;
  created: boolean;
}

/* ---------------------------------------------------------------- accepting and rejecting */

/**
 * The Log tab's suggestion actions (asset.edit_metadata):
 *  - POST /api/v1/assets/:id/segments/:segmentId/accept → the segment (accepted_by/at set);
 *  - POST /api/v1/assets/:id/segments/accept-all {stratumId} → AcceptAllResult;
 *  - POST /api/v1/assets/:id/segments/:segmentId/reject → 204 (the suggestion is deleted);
 *  - PATCH …/segments/:segmentId (the existing edit) accepts a suggestion as it saves it.
 * 409 NOT_A_SUGGESTION for a segment that is not one. Audited; asset.segments_changed.
 */
export const AcceptAllInput = z.object({ stratumId: z.string().regex(/^s_[a-z0-9_]{1,40}$/) }).strict();
export interface AcceptAllResult { accepted: number }

/** GET /api/v1/assets/:id/segments?suggestions=only|exclude (default include). Search: `acceptedOnly` (default false). */
export const SEGMENT_SUGGESTION_FILTERS = ['include', 'only', 'exclude'] as const;

/** A metadata suggestion (metadata_suggestions, 0100): a value for a field to accept or reject. */
export interface MetadataSuggestionDTO {
  id: string;
  assetId: string;
  fieldId: string;
  fieldLabel: string;
  value: unknown;
  /** The field's value now, to compare. */
  current: unknown;
  suggestedBy: string;
  suggestedAt: string;
}

/**
 * GET /api/v1/assets/:id/metadata-suggestions (asset.read) → {items}; POST …/metadata-suggestions/:sid/accept and
 * …/reject (asset.edit_metadata). Accepting sets the field as an edit by the person (revision, field rules, audit).
 * One pending suggestion per asset and field: a newer one replaces it.
 */
export const MetadataSuggestionInput = z.object({
  values: z.record(z.string().min(1).max(80), z.unknown()),
}).strict();

/* ---------------------------------------------------------------- signed media links */

/**
 * The internal route the webhook activity resolves bound link calls with (A):
 * POST /internal/v1/automation/runs/:runId/media-links {nodeId, endpointId, ttlSeconds, calls} → MediaLinksResult.
 * Refused for an endpoint without may_receive_media_links (422 MEDIA_LINKS_NOT_ALLOWED). Each link is a row of
 * automation_media_links (0100), so its uses are counted and logged.
 */
export const MediaLinksRequest = z.object({
  nodeId: z.string().max(80),
  endpointId: z.string().uuid(),
  ttlSeconds: z.number().int().min(MEDIA_LINK_TTL.minSeconds).max(MEDIA_LINK_TTL.maxSeconds).default(MEDIA_LINK_TTL.defaultSeconds),
  calls: z.array(z.object({ name: z.string().max(40), args: z.array(z.union([z.string().max(2000), z.number(), z.boolean(), z.null()])).max(4), token: z.string().max(4000) })).max(1000),
}).strict();
export type MediaLinksRequest = z.infer<typeof MediaLinksRequest>;
export interface MediaLinksResult {
  /** token → the URL (or base64 text for base64Frame). */
  resolved: Record<string, string>;
  expiresAt: string;
}
/** The media link route (served on the media origin): GET /media/l/:token. 410 MEDIA_LINK_EXPIRED once past its time. */
export const MEDIA_LINK_ROUTE = '/media/l/:token';
export const MEDIA_LINK_KINDS: readonly LinkFunctionName[] = ['shotFrameUrl', 'proxyUrl', 'keyframeUrl', 'exportFileUrl', 'subtitleUrl', 'base64Frame'];

/* ---------------------------------------------------------------- example flows */

/** The four example flows (Mikael, 2026-10-25): one per provider; OpenRouter's also works against Ollama or vLLM. */
export const AI_FLOW_PROVIDERS = ['claude', 'openai', 'gemini', 'openrouter'] as const;
export type AiFlowProvider = (typeof AI_FLOW_PROVIDERS)[number];
